import React from 'react'
import PropTypes from 'prop-types'
import {
  HORIZONTAL_SELECT_ONE_MAX_CHOICES,
  HORIZONTAL_SELECT_ONE_MIN_CHOICES,
  CONSENT_QUESTION_TYPE_OPTIONS,
  QUESTION_TYPE_MAP,
  SURVEY_QUESTION_TYPE_OPTIONS,
} from 'utils/constants'
import { Dropdown, Popup, Button, Checkbox } from 'components/UIElements'
import { runNTimes } from 'utils/misc'
import useFoodDiaryFeature from 'utils/hooks/useFoodDiaryFeature'

const shortExample = require('../assets/short.png')
const medExample = require('../assets/medium.png')
const longExample = require('../assets/long.png')

const options = SURVEY_QUESTION_TYPE_OPTIONS
const consentOptions = CONSENT_QUESTION_TYPE_OPTIONS

const SIZE_OPTIONS = [
  { key: 'short', value: 'short', text: 'Short' },
  { key: 'medium', value: 'medium', text: 'Medium' },
  { key: 'long', value: 'long', text: 'Long' },
]

const QuestionPanel = ({
  activeItemId,
  item,
  onChangeItemType,
  onUpdateItemInputWidth,
  onAddChoice,
  onAddMatrixQuestion,
  onDeleteMatrixQuestion,
  onUpdateUnitType,
  onDeleteChoice,
  toggleMatrixAllowMultiple,
  isConsent,
  study,
  userId,
}) => {
  const hasChoices = [
    'select_one',
    'select_multiple',
    'likert',
    'long_list_select_one',
    'numeric_rating_scale',
    QUESTION_TYPE_MAP.horizontalSelectOne,
    'multiple_field',
    QUESTION_TYPE_MAP.matrix,
  ].includes(item.type)
  const isNRS = item.type === QUESTION_TYPE_MAP.numericRatingScale
  const isMultiField = item.type === QUESTION_TYPE_MAP.multipleField
  const isLongList = item.type === QUESTION_TYPE_MAP.longListSelectOne
  const isMatrix = item.type === QUESTION_TYPE_MAP.matrix
  const hasQuestions = isMatrix

  const { hasFoodDiary } = useFoodDiaryFeature({ study, userId })
  const isSibling = !!item.sibling_id

  const filteredOptions = (hasFoodDiary ? options : options.filter(option => option.key !== QUESTION_TYPE_MAP.foodEntry)).filter(
    option => !isSibling || option.key !== QUESTION_TYPE_MAP.timedMatchingGrid,
  )

  const checkIsMinusBtnDisabled = () => {
    if (item.type === QUESTION_TYPE_MAP.horizontalSelectOne) {
      return item.choices_order.length <= HORIZONTAL_SELECT_ONE_MIN_CHOICES
    }
    return item.choices_order.length <= (isMultiField ? 1 : isLongList ? 10 : 2)
  }

  const checkIsPlusBtnDisabled = () => {
    if (item.type === QUESTION_TYPE_MAP.horizontalSelectOne) {
      return item.choices_order.length >= HORIZONTAL_SELECT_ONE_MAX_CHOICES
    }
    return (item.choices_order.length >= 11 && isNRS) || (isMatrix && item.choices_order.length >= 7)
  }

  const choicesSection = (
    <div className='choices section'>
      <h5 className='label-small med-font'>
        {isMultiField ? 'Input fields with labels' : isMatrix ? 'Number of choices (columns)' : 'Options'}
      </h5>
      {hasChoices && item.type !== 'likert' ? (
        <div>
          <Button
            className='inline flexed'
            onClick={onDeleteChoice(item.choices_order[item.choices_order.length - 1])}
            circle
            disabled={checkIsMinusBtnDisabled()}
            id='minus-options'
            icon='fas fa-minus'
          />
          <p className='inline flexed'>{item.choices_order.length}</p>
          <Button
            className='inline flexed'
            disabled={checkIsPlusBtnDisabled()}
            onClick={onAddChoice}
            circle
            id='plus-options'
            icon='fas fa-plus'
          />
        </div>
      ) : (
        <div>
          <Button
            className='inline flexed'
            onClick={
              item.choices_order.length > 5
                ? () => runNTimes(2, onDeleteChoice(item.choices_order[item.choices_order.length - 1]))
                : null
            }
            circle
            disabled={item.choices_order.length <= 5}
            icon='fas fa-minus'
          />
          <p className='inline flexed'>{item.choices_order.length}</p>
          <Button
            className='inline flexed'
            onClick={item.choices_order.length === 5 ? () => runNTimes(2, onAddChoice) : null}
            circle
            disabled={item.choices_order.length >= 7}
            icon='fas fa-plus'
          />
        </div>
      )}
    </div>
  )

  const renderQuestionsSection = () => (
    <div className='choices section'>
      <p>Number of questions (rows)</p>
      <div>
        <Button
          className='inline flexed'
          onClick={() => onDeleteMatrixQuestion(item.questions_order[item.questions_order.length - 1])}
          circle
          disabled={item.questions_order.length <= 1}
          icon='fas fa-minus'
        />
        <p className='inline flexed'>{item.questions_order.length}</p>
        <Button
          className='inline flexed'
          disabled={isMatrix && item.questions_order?.length >= 12}
          onClick={onAddMatrixQuestion}
          circle
          icon='fas fa-plus'
        />
      </div>
    </div>
  )

  const renderMatrixAllowMultiAnswerCheckbox = () => (
    <div className='section'>
      <Checkbox
        className='matrix-allow-multi-checkbox'
        checked={item.attributes.allowMultipleAnswers}
        label='Allow multiple answers per question'
        onClick={() => toggleMatrixAllowMultiple(activeItemId)}
      />
    </div>
  )

  const unitsSection = ['decimal', 'integer'].includes(item.type) ? (
    <div className='number section'>
      Unit:
      <input onChange={onUpdateUnitType} maxLength={20} value={item.attributes.unit || ''} />
    </div>
  ) : null

  const renderSamplePicture = (srcImage, text, alt) => {
    return (
      <div className='flexed column'>
        <img src={srcImage} alt={alt} />
        <b>{text}</b>
      </div>
    )
  }

  const inputWidthSection = (
    <div className='input-width section'>
      <div className='flexed start-justified'>
        <div className='label-small'>Size of input fields</div>
        <Popup
          className='width-examples'
          hover
          overrideOverflow
          noPointer
          dark
          position='top'
          trigger={<i className='fas fa-info-circle' />}>
          <p>Examples of different sizes of input fields displayed to participants.</p>
          <div className='flexed'>
            {renderSamplePicture(shortExample, 'Short', 'short-field-example')}
            {renderSamplePicture(medExample, 'Medium', 'medium-field-example')}
            {renderSamplePicture(longExample, 'Long', 'long-field-example')}
          </div>
        </Popup>
      </div>
      <Dropdown
        className='input-width'
        selected={item.attributes.inputWidth}
        options={SIZE_OPTIONS}
        onSelect={onUpdateItemInputWidth}
      />
    </div>
  )

  return (
    <div>
      <>
        <div className='question panel'>
          <h5 className='label-small med-font'>Question Type</h5>
          <div className='selector section'>
            <Dropdown
              className='select-dropdown'
              id='select-question-type'
              onSelect={onChangeItemType}
              options={isConsent ? consentOptions : filteredOptions}
              selected={item.type}
              sortingOrder='asc'
              sortingBy='text'
            />
          </div>
          {isMatrix && renderMatrixAllowMultiAnswerCheckbox()}
          {hasChoices && choicesSection}
          {hasQuestions && renderQuestionsSection()}
          {isMultiField && inputWidthSection}
          {unitsSection}
        </div>
      </>
    </div>
  )
}

QuestionPanel.propTypes = {
  activeItemId: PropTypes.string,
  isConsent: PropTypes.bool,
  item: PropTypes.shape({
    choices_order: PropTypes.array,
    questions_order: PropTypes.array,
    sibling_id: PropTypes.string,
    type: PropTypes.string,
    attributes: PropTypes.shape({
      allowMultipleAnswers: PropTypes.bool,
    }),
  }),
  onAddChoice: PropTypes.func,
  onAddMatrixQuestion: PropTypes.func,
  onChangeItemType: PropTypes.func,
  onDeleteChoice: PropTypes.func,
  onDeleteMatrixQuestion: PropTypes.func,
  onUpdateItemInputWidth: PropTypes.func,
  onUpdateUnitType: PropTypes.func,
  toggleMatrixAllowMultiple: PropTypes.func,
}

export default QuestionPanel
